/**
 * Transports list screen  –  ticket #778
 *
 * Features:
 * - List all transports with item count
 * - Create / edit / delete transports
 * - Open detail panel to manage items in a transport
 * - Export (download zip) button per transport
 */
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faDownload, faTrash, faXmark, faTruck } from '@fortawesome/free-solid-svg-icons';
import { AppDispatch, RootState, useAppDispatch } from 'redux/store';
import FormatDateTime from 'components/common/FormatDateTime';
import {
  fetchTransports,
  fetchTransport,
  createTransport,
  updateTransport,
  deleteTransport,
  removeTransportItem,
  Transport,
} from 'redux/slices/transportsSlice';
import api from 'services/api';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import TransportActionsModal from './TransportActionsModal';

// ---------------------------------------------------------------------------
const OBJECT_TYPE_LABELS: Record<string, string> = {
  node_definition: 'Node Definition',
  lock: 'Lock',
  trigger: 'Trigger',
  event: 'Event',
  scheduling_window: 'Scheduling Window',
  config_item: 'Config Item',
  recurrence_definition: 'Recurrence Definition',
  integration: 'Integration',
  python_library: 'Python Library',
  role: 'Role',
  user: 'User',
};

// ---------------------------------------------------------------------------
const ListTransports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { transports, selectedTransport, loading } = useSelector((s: RootState) => s.transports);

  // ── modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTransport, setEditingTransport] = useState<Transport | null>(null);
  const [deletingTransport, setDeletingTransport] = useState<Transport | null>(null);
  const [detailTransport, setDetailTransport] = useState<Transport | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);

  // ── form fields
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [showTransportCloseConfirm, setShowTransportCloseConfirm] = useState(false);
  const [showCreateCloseConfirm, setShowCreateCloseConfirm] = useState(false);
  const [transportActionsTransport, setTransportActionsTransport] = useState<Transport | null>(null);

  const isDirtyTransport = useUnsavedChanges(!!editingTransport, {
    formName,
    formDescription,
  });

  const isDirtyCreate = useUnsavedChanges(showCreateModal, {
    formName,
    formDescription,
  });

  const handleRequestCloseEdit = () => {
    if (isDirtyTransport) {
      setShowTransportCloseConfirm(true);
    } else {
      setEditingTransport(null);
    }
  };

  const handleRequestCloseCreate = () => {
    if (isDirtyCreate) {
      setShowCreateCloseConfirm(true);
    } else {
      setShowCreateModal(false);
    }
  };

  useEffect(() => {
    dispatch(fetchTransports());
  }, [dispatch]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Transports | Norscom';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // ── open detail panel
  const handleOpenDetail = (t: Transport) => {
    dispatch(fetchTransport(t.id));
    setDetailTransport(t);
    setSelectedItemIds([]);
  };

  useEffect(() => {
    if (selectedTransport && detailTransport && selectedTransport.id === detailTransport.id) {
      setDetailTransport(selectedTransport);
    }
  }, [selectedTransport]);

  // ── create
  const openCreate = () => {
    setFormName('');
    setFormDescription('');
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!formName.trim()) return;
    await dispatch(
      createTransport({
        name: formName.trim(),
        description: formDescription || undefined,
      })
    );
    setShowCreateModal(false);
  };

  // ── edit
  const openEdit = (t: Transport) => {
    setFormName(t.name);
    setFormDescription(t.description ?? '');
    setEditingTransport(t);
  };

  const handleEdit = async () => {
    if (!editingTransport || !formName.trim()) return;
    await dispatch(
      updateTransport({
        id: editingTransport.id,
        name: formName.trim(),
        description: formDescription || undefined,
      })
    );
    setEditingTransport(null);
  };

  // ── delete
  const handleDelete = async () => {
    if (!deletingTransport) return;
    await dispatch(deleteTransport(deletingTransport.id));
    setDeletingTransport(null);
    if (detailTransport?.id === deletingTransport.id) setDetailTransport(null);
  };

  // ── export
  const handleExport = async (t: Transport) => {
    try {
      const res = await api.get(`/transports/${t.id}/export`, {
        responseType: 'blob',
      });

      const contentType = res.headers?.['content-type'] || '';
      if (!contentType.includes('application/zip')) {
        let message = 'Export failed: server returned non-zip response';
        try {
          const text = await res.data?.text?.();
          if (text) message = `${message} (${text.slice(0, 180)})`;
        } catch {
          // keep default message
        }
        throw new Error(message);
      }

      const blob = res.data as Blob;
      if (!blob || blob.size === 0) {
        throw new Error('Export failed: empty zip file.');
      }

      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `${t.name}.zip`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message;
      // Keep it simple here; avoid saving invalid files with .zip extension.
      window.alert(backendMsg || 'Failed to export transport zip.');
    }
  };

  // ── remove item from transport
  const handleRemoveItem = async (transportId: number, itemId: number) => {
    await dispatch(removeTransportItem({ transportId, itemId }));
    dispatch(fetchTransport(transportId));
  };

  const handleToggleItemSelection = (itemId: number) => {
    setSelectedItemIds((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  const handleToggleSelectAll = (itemIds: number[]) => {
    if (selectedItemIds.length === itemIds.length) {
      setSelectedItemIds([]);
      return;
    }
    setSelectedItemIds(itemIds);
  };

  const handleRemoveSelectedItems = async (transportId: number) => {
    if (selectedItemIds.length === 0) return;
    await Promise.all(selectedItemIds.map((itemId) => dispatch(removeTransportItem({ transportId, itemId }))));
    setSelectedItemIds([]);
    dispatch(fetchTransport(transportId));
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Transports</h4>
        <Button variant="primary" size="sm" onClick={openCreate}>
          + New Transport
        </Button>
      </div>

      {loading.fetchTransports ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <Row>
          {/* ── Transport list ───────────────────────────── */}
          <Col md={detailTransport ? 5 : 12}>
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Items</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transports.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No transports yet.
                    </td>
                  </tr>
                )}
                {transports.map((t) => (
                  <tr
                    key={t.id}
                    className={detailTransport?.id === t.id ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleOpenDetail(t)}
                  >
                    <td>{t.name}</td>
                    <td className="text-muted small">{t.description ?? '—'}</td>
                    <td>
                      <Badge bg="secondary">{t.item_count ?? 0}</Badge>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEdit(t)} title="Edit">
                        <FontAwesomeIcon icon={faPen} />
                      </Button>
                      <Button variant="outline-success" size="sm" className="me-1" onClick={() => handleExport(t)} title="Export zip">
                        <FontAwesomeIcon icon={faDownload} />
                      </Button>
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => setTransportActionsTransport(t)} title="Transport actions">
                        <FontAwesomeIcon icon={faTruck} />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => setDeletingTransport(t)} title="Delete">
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>

          {/* ── Detail panel ─────────────────────────────── */}
          {detailTransport && (
            <Col md={7}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <span>
                      <strong>{detailTransport.name}</strong> – Items
                    </span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={selectedItemIds.length === 0 || loading.removeItem}
                      onClick={() => handleRemoveSelectedItems(detailTransport.id)}
                    >
                      {loading.removeItem ? <Spinner size="sm" /> : `Remove selected (${selectedItemIds.length})`}
                    </Button>
                  </div>
                  <Button variant="link" size="sm" onClick={() => setDetailTransport(null)}>
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading.fetchTransport ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <Table hover striped bordered responsive size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <Form.Check
                              type="checkbox"
                              checked={(detailTransport.items ?? []).length > 0 && selectedItemIds.length === (detailTransport.items ?? []).length}
                              onChange={() => handleToggleSelectAll((detailTransport.items ?? []).map((i) => i.id))}
                            />
                          </th>
                          <th style={{ width: 60 }}>#</th>
                          <th>Type</th>
                          <th>Object</th>
                          <th>Added At</th>
                          <th style={{ width: 110 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailTransport.items ?? []).length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3">
                              No items in this transport.
                            </td>
                          </tr>
                        )}
                        {(detailTransport.items ?? []).map((item, index) => (
                          <tr key={item.id}>
                            <td>
                              <Form.Check type="checkbox" checked={selectedItemIds.includes(item.id)} onChange={() => handleToggleItemSelection(item.id)} />
                            </td>
                            <td>{index + 1}</td>
                            <td>
                              <Badge bg="info" text="dark">
                                {OBJECT_TYPE_LABELS[item.object_type] ?? item.object_type}
                              </Badge>
                            </td>
                            <td>{item.object_name ? `${item.object_name} (${item.object_id})` : `ID ${item.object_id}`}</td>
                            <td className="text-muted">{item.created_at ? <FormatDateTime value={item.created_at} /> : '—'}</td>
                            <td>
                              <Button variant="outline-danger" size="sm" onClick={() => handleRemoveItem(detailTransport.id, item.id)}>
                                <FontAwesomeIcon icon={faTrash} className="me-1" />
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* ── Create modal ──────────────────────────────────────────────── */}
      <Modal show={showCreateModal} onHide={handleRequestCloseCreate} centered>
        <Modal.Header closeButton>
          <Modal.Title>New Transport</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g. release-2026-q1" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestCloseCreate}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={!formName.trim() || loading.createTransport}>
            {loading.createTransport ? <Spinner size="sm" /> : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit modal ────────────────────────────────────────────────── */}
      <Modal show={!!editingTransport} onHide={handleRequestCloseEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Transport</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control type="text" value={formName} onChange={(e) => setFormName(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestCloseEdit}>
            Close
          </Button>
          <Button variant="primary" onClick={handleEdit} disabled={!formName.trim() || loading.updateTransport}>
            {loading.updateTransport ? <Spinner size="sm" /> : 'Save and Close'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete confirmation ───────────────────────────────────────── */}
      <Modal show={!!deletingTransport} onHide={() => setDeletingTransport(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Transport</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete transport <strong>{deletingTransport?.name}</strong>? This will also remove all associated items.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingTransport(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading.deleteTransport}>
            {loading.deleteTransport ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showTransportCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowTransportCloseConfirm(false);
          setEditingTransport(null);
        }}
        onCancel={() => setShowTransportCloseConfirm(false)}
      />

      <ConfirmationModal
        show={showCreateCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCreateCloseConfirm(false);
          setShowCreateModal(false);
        }}
        onCancel={() => setShowCreateCloseConfirm(false)}
      />

      {transportActionsTransport && (
        <TransportActionsModal show={!!transportActionsTransport} onClose={() => setTransportActionsTransport(null)} transport={transportActionsTransport} />
      )}
    </div>
  );
};

export default ListTransports;
